import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ConnectionService } from '../../service/connection.service';
import { EventService } from '../../service/event.service';
import { SwalService } from '../../service/swal.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ContractSettingsComponent } from '../../components/contract-settings/contract-settings.component';
import { IconComponent } from '../../components/icon/icon.component';
import { LoadErrorComponent } from '../../components/load-error/load-error.component';

type SignStatus = 'signed' | 'waiting' | 'todo';

@Component({
  selector: 'app-contratti',
  templateUrl: './contratti.component.html',
  imports: [CommonModule, FormsModule, RouterModule, TranslatePipe, ContractSettingsComponent, IconComponent, LoadErrorComponent],
  styleUrls: ['./contratti.component.css'],
})
export class ContrattiComponent implements OnInit {
  activeTab = 0; // 0 = contratti generati, 1 = impostazioni contratto
  contracts: any[] = [];
  searchText = '';
  selectedYear: number | null = null;
  regenerating: string | null = null; // id in corso di rigenera
  menuOpenId: string | null = null;

  constructor(
    private connection: ConnectionService,
    private ms: EventService,
    private swal: SwalService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadContracts();
    if (localStorage.getItem('contract_wizard_pending') === 'true') {
      this.activeTab = 1;
    }
  }

  // Contratti non arrivati: errore con Riprova, non un falso "nessun contratto"
  loadError = false;

  loadContracts() {
    this.connection.get(
      'events/contracts',
      (data: any[]) => {
        this.loadError = false;
        // Filtra SOLO quelli con contract.file presente
        this.contracts = data
          .filter((c) => !!c.event?.contract?.file)
          .map((c) => ({
            id: c._id,
            nome: `${c.client.name} ${c.client.lastname}`,
            data: c.event.date,
            contractFile: c.event.contract.file,
            status: this.signStatus(c),
          }))
          .sort(
            (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime(),
          );
      },
      () => (this.loadError = true),
    );
  }

  private signStatus(c: any): SignStatus {
    const sr = c.signature_request;
    if (c.event?.contract?.is_signed || sr?.signed_at) return 'signed';
    if (sr?.expires_at && new Date(sr.expires_at) > new Date()) return 'waiting';
    return 'todo';
  }

  toggleMenu(id: string): void {
    this.menuOpenId = this.menuOpenId === id ? null : id;
  }

  // ── STATISTICHE ───────────────────────────────────────────────────────────

  get totalContracts(): number {
    return this.contracts.length;
  }

  get thisMonthContracts(): number {
    const now = new Date();
    return this.contracts.filter((c) => {
      const d = new Date(c.data);
      return (
        d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
      );
    }).length;
  }

  get thisYearContracts(): number {
    const year = new Date().getFullYear();
    return this.contracts.filter((c) => new Date(c.data).getFullYear() === year)
      .length;
  }

  // ── FILTRI ────────────────────────────────────────────────────────────────

  get years(): number[] {
    return [
      ...new Set(this.contracts.map((c) => new Date(c.data).getFullYear())),
    ].sort((a, b) => b - a);
  }

  get filteredContracts(): any[] {
    return this.contracts.filter((c) => {
      const matchYear =
        !this.selectedYear ||
        new Date(c.data).getFullYear() === this.selectedYear;
      const matchSearch = c.nome
        .toLowerCase()
        .includes(this.searchText.toLowerCase());
      return matchYear && matchSearch;
    });
  }

  // ── INDEX CARD ────────────────────────────────────────────────────────────

  getCardIndex(contratto: any): string {
    const year = new Date(contratto.data).getFullYear();
    const sorted = this.contracts
      .filter((c) => new Date(c.data).getFullYear() === year)
      .sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime());
    const pos = sorted.findIndex((c) => c.id === contratto.id) + 1;
    return `${year} · ${pos}`;
  }

  // ── DOWNLOAD / VIEW ───────────────────────────────────────────────────────

  download(contractId: string) {
    this.connection.getBlob(
      `events/download_contract/${contractId}`,
      (blob: Blob) => {
        const url = URL.createObjectURL(
          new Blob([blob], { type: 'application/pdf' }),
        );
        const a = document.createElement('a');
        a.href = url;
        a.download = `contratto-${contractId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      (err: any) => console.error('Errore download PDF', err),
    );
  }

  viewContract(contractId: string) {
    this.connection.getBlob(
      `events/download_contract/${contractId}`,
      (blob: Blob) => {
        const url = URL.createObjectURL(
          new Blob([blob], { type: 'application/pdf' }),
        );
        window.open(url, '_blank');
      },
      (err: any) => console.error('Errore visualizzazione PDF', err),
    );
  }

  // ── RIGENERA ──────────────────────────────────────────────────────────────

  rigenera(contratto: any) {
    this.swal
      .confirm(
        this.translate.instant('contracts.regenerate_title'),
        this.translate.instant(
          contratto.status === 'signed' ? 'riepilogo.regenerate_signed_text' : 'contracts.regenerate_text',
          { name: contratto.nome },
        ),
      )
      .then((result) => {
        if (!result.isConfirmed) return;

        this.regenerating = contratto.id;
        this.ms.buildContratto(contratto.id, (blob, ctx) => {
          this.regenerating = null;
          this.setPreview(contratto, blob, ctx.signed, ctx.lang);
        });
        setTimeout(() => {
          if (this.regenerating === contratto.id) this.regenerating = null;
        }, 30000);
      });
  }

  previewOpen = false;
  previewUrl: SafeResourceUrl | null = null;
  private previewObjectUrl: string | null = null;
  private previewBlob: Blob | null = null;
  private previewSigned = false;
  private previewLang = 'it';
  previewContratto: any = null;
  finalizing = false;

  private setPreview(
    contratto: any,
    blob: Blob,
    signed: boolean,
    lang: string,
  ) {
    this.clearPreviewUrl();
    this.previewContratto = contratto;
    this.previewBlob = blob;
    this.previewSigned = signed;
    this.previewLang = lang;
    this.previewObjectUrl = URL.createObjectURL(blob);
    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.previewObjectUrl,
    );
    this.previewOpen = true;
  }

  finalizzaContratto() {
    if (!this.previewBlob || !this.previewContratto) return;
    this.finalizing = true;
    this.ms.finalizeContratto(
      this.previewContratto.id,
      this.previewLang,
      this.previewSigned,
      () => {
        this.finalizing = false;
        this.closePreview();
        this.loadContracts();
      },
    );
  }

  closePreview() {
    this.previewOpen = false;
    this.clearPreviewUrl();
    this.previewBlob = null;
    this.previewContratto = null;
  }

  private clearPreviewUrl() {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
    this.previewUrl = null;
  }
}
